import { useState, useEffect, useContext, useMemo } from 'react';
import { StudioContext } from '../../studios/StudioContext';
import './Videos.css';

// NOTE: assumes StudioContext config exposes an API base the same way other
// non-demo components read it (mirrors the `${API}/passes/verify` pattern in
// EventModal.jsx). If the field name isn't `config.api`, this is a one-line fix.
export default function Videos({ embedOnly = false }) {
  const config = useContext(StudioContext);
  const API = config?.apiBaseUrl;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [viewMode, setViewMode] = useState('card'); // 'card' | 'list'
  const [subjectFilter, setSubjectFilter] = useState('all');

  // Email gate — mirrors EventModal's passCode/checking/codeStatus shape
  const [email, setEmail] = useState('');
  const [gateInput, setGateInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [gateStatus, setGateStatus] = useState(null); // null | 'unlocked'

  // Per-video access state, keyed by video _id: 'checking' | 'watch' | 'rent'
  const [access, setAccess] = useState({});
  const [rentingId, setRentingId] = useState(null);
  const [playerUrl, setPlayerUrl] = useState(null);

  // Rental coupon codes (one code box open at a time)
  const [codeFor, setCodeFor] = useState(null);      // video _id with code box open
  const [codeInput, setCodeInput] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [quote, setQuote] = useState(null);          // { videoId, code, priceCents }
  const [rentMsg, setRentMsg] = useState(null);      // { videoId, ok, text }
  const [basePriceCents, setBasePriceCents] = useState(1500); // this email's rental price

  // 2x/week members get their rate automatically
  useEffect(() => {
    if (!API || !email) { setBasePriceCents(1500); return; }
    fetch(`${API}/videos/rent-price?email=${encodeURIComponent(email)}`)
      .then(r => r.json())
      .then(d => { if (typeof d.priceCents === 'number') setBasePriceCents(d.priceCents); })
      .catch(() => {});
  }, [API, email]);

  useEffect(() => {
    if (!API) {
      setLoadError('Video library is not configured correctly.');
      setLoading(false);
      return;
    }
    fetch(`${API}/videos`)
      .then((res) => res.json())
      .then((data) => setVideos(Array.isArray(data) ? data : []))
      .catch(() => setLoadError('Could not load videos. Please try again.'))
      .finally(() => setLoading(false));
  }, [API]);

  const subjects = useMemo(() => {
    const set = new Set(videos.map((v) => v.subject).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [videos]);

  const visibleVideos = useMemo(() => {
    const filtered =
      subjectFilter === 'all' ? videos : videos.filter((v) => v.subject === subjectFilter);
    // newest first — classDate falls back to createdAt
    return [...filtered].sort((a, b) => {
      const da = a.classDate || a.createdAt || '';
      const db = b.classDate || b.createdAt || '';
      return db.localeCompare(da);
    });
  }, [videos, subjectFilter]);

  // Once email is unlocked, check /:id/play for every visible video to
  // decide Watch vs Rent state per card.
  useEffect(() => {
    if (!email || !API || visibleVideos.length === 0) return;
    let cancelled = false;

    setAccess((prev) => {
      const next = { ...prev };
      visibleVideos.forEach((v) => {
        if (!next[v._id]) next[v._id] = 'checking';
      });
      return next;
    });

    Promise.allSettled(
      visibleVideos.map((v) =>
        fetch(`${API}/videos/${v._id}/play?email=${encodeURIComponent(email)}`).then((res) =>
          res.ok ? res.json().then((data) => ({ id: v._id, ok: true, data })) : { id: v._id, ok: false }
        )
      )
    ).then((results) => {
      if (cancelled) return;
      setAccess((prev) => {
        const next = { ...prev };
        results.forEach((r) => {
          if (r.status !== 'fulfilled') return;
          const { id, ok } = r.value;
          next[id] = ok ? 'watch' : 'rent';
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [email, API, visibleVideos]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    const trimmed = gateInput.trim();
    if (!trimmed) return;
    setChecking(true);
    // No dedicated "check this email is real" endpoint exists — unlocking
    // just reveals per-card state, which the /play calls above resolve.
    setEmail(trimmed.toLowerCase());
    setGateStatus('unlocked');
    setChecking(false);
  };

  const handleWatch = async (video) => {
    const result = access[video._id];
    if (result !== 'watch') return;
    try {
      const res = await fetch(`${API}/videos/${video._id}/play?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      if (res.ok) setPlayerUrl(data.url);
    } catch {
      // swallow — card state already reflects rent/watch, a transient
      // failure here just means the click does nothing
    }
  };

  const openCode = (id) => {
    setCodeFor(id);
    setCodeInput('');
    setQuote(null);
    setRentMsg(null);
  };

  const applyCode = async (video) => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeChecking(true);
    setRentMsg(null);
    try {
      const res = await fetch(`${API}/videos/${video._id}/rent-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, couponCode: code }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        setQuote(null);
        setRentMsg({ videoId: video._id, ok: false, text: data.error || 'Invalid code.' });
        return;
      }
      setQuote({ videoId: video._id, code: data.code, priceCents: data.priceCents });
      setRentMsg({
        videoId: video._id,
        ok: true,
        text: !data.couponBetter
          ? 'Your membership rate is already better'
          : data.priceCents === 0
            ? 'Free with code'
            : `${fmtCents(data.originalCents - data.priceCents)} off`,
      });
    } catch {
      setRentMsg({ videoId: video._id, ok: false, text: 'Could not check code.' });
    } finally {
      setCodeChecking(false);
    }
  };

  const fmtCents = (c) => (c % 100 === 0 ? `$${c / 100}` : `$${(c / 100).toFixed(2)}`);

  const rentLabel = (video) => {
    if (quote?.videoId !== video._id) return `Rent — ${fmtCents(basePriceCents)}`;
    return quote.priceCents === 0 ? 'Watch free' : `Rent — ${fmtCents(quote.priceCents)}`;
  };

  const handleRent = async (video) => {
    // Code typed but not applied yet: check it first so the price updates before checkout
    if (codeFor === video._id && codeInput.trim() && quote?.videoId !== video._id) {
      await applyCode(video);
      return;
    }
    setRentingId(video._id);
    try {
      const res = await fetch(`${API}/videos/${video._id}/rent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          ...(quote?.videoId === video._id ? { couponCode: quote.code } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRentMsg({ videoId: video._id, ok: false, text: data.error || 'Could not start rental.' });
        return;
      }
      if (data.alreadyIncluded && data.url) {
        setPlayerUrl(data.url);
        if (data.freeWithCode) {
          setAccess(a => ({ ...a, [video._id]: 'watch' }));
          setQuote(null);
          setCodeFor(null);
          setRentMsg(null);
        }
      } else if (data.url) {
        window.top.location.href = data.url; // must break out of the video-library iframe embed
      }
    } catch {
      // no-op — button just stays in its current state
    } finally {
      setRentingId(null);
    }
  };

  // NOTE: reuses the existing generic subscribe route per the Aug 30 notes
  // (`/api/passes/subscribe`, videoSub tier) — confirm the exact payload
  // shape against whatever calls it for other pass types before wiring this
  // fully; left as a direct POST with the fields that route is documented
  // to expect.
  const handleSubscribe = async () => {
    if (!email) {
      setGateStatus(null);
      return;
    }
    try {
      const res = await fetch(`${API}/passes/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'videoSub' }),
      });
      const data = await res.json();
      if (data.url) window.top.location.href = data.url;
    } catch {
      // no-op
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className={`videos-page ${embedOnly ? 'videos-page--embed' : ''}`}>
      {!embedOnly && (
        <div className="videos-page__header">
          <p className="videos-page__eyebrow">On-demand classes</p>
          <h1 className="videos-page__title">Video Library</h1>
        </div>
      )}

      <div className="subscribe-banner">
        <span>Unlimited access to every class recording — $99/month</span>
        <button className="adm-btn--primary" onClick={handleSubscribe}>
          Subscribe
        </button>
      </div>

      {gateStatus !== 'unlocked' && (
        <form className="video-gate" onSubmit={handleUnlock}>
          <input
            type="email"
            placeholder="Enter your email to unlock class recordings"
            value={gateInput}
            onChange={(e) => setGateInput(e.target.value)}
            required
          />
          <button type="submit" disabled={checking}>
            {checking ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      )}

      <div className="videos-toolbar">
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
            onClick={() => setViewMode('card')}
          >
            Card
          </button>
          <button
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>

        {subjects.length > 1 && (
          <select
            className="subject-filter"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            {subjects.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All subjects' : s}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading && <p className="videos-status">Loading videos…</p>}
      {loadError && <p className="videos-status videos-status--error">{loadError}</p>}
      {!loading && !loadError && visibleVideos.length === 0 && (
        <div className="videos-coming-soon">
          <h2>Coming Soon</h2>
          <p>We're building out the library — check back shortly for class recordings you can rent or stream anytime.</p>
        </div>
      )}

      <div className={viewMode === 'card' ? 'videos-grid' : 'videos-list'}>
        {visibleVideos.map((video) => {
          const state = gateStatus === 'unlocked' ? access[video._id] : null;
          return (
            <div key={video._id} className={viewMode === 'card' ? 'video-card' : 'video-row'}>
              <div className="video-thumb">
                {video.thumbnailUrl ? (
                  <img src={video.thumbnailUrl} alt={video.title} />
                ) : (
                  <div className="video-thumb--placeholder" />
                )}
              </div>
              <div className="video-info">
                <p className="video-title">{video.title}</p>
                <p className="video-meta">
                  {formatDate(video.classDate)}
                  {video.subject ? ` · ${video.subject}` : ''}
                </p>
              </div>
              <div className="video-action">
                {!state && gateStatus !== 'unlocked' && (
                  <span className="video-action__hint">Enter email to unlock</span>
                )}
                {state === 'checking' && <span className="video-action__hint">Checking…</span>}
                {state === 'watch' && (
                  <button className="adm-btn--primary" onClick={() => handleWatch(video)}>
                    Watch
                  </button>
                )}
                {state === 'rent' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
                    <button
                      className="adm-btn--primary"
                      disabled={rentingId === video._id}
                      onClick={() => handleRent(video)}
                    >
                      {rentingId === video._id ? 'Loading…' : rentLabel(video)}
                    </button>
                    {codeFor !== video._id ? (
                      <button
                        type="button"
                        onClick={() => openCode(video._id)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          fontSize: '0.8rem', textDecoration: 'underline', color: 'inherit', opacity: 0.75 }}
                      >
                        Have a code?
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.35rem', width: '100%', justifyContent: 'flex-end' }}>
                        <input
                          className="adm-input"
                          value={codeInput}
                          placeholder="Code"
                          onChange={e => setCodeInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') applyCode(video); }}
                          style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '9rem', textTransform: 'uppercase', boxSizing: 'border-box' }}
                        />
                        <button
                          type="button"
                          className="adm-btn-export"
                          disabled={!codeInput.trim() || codeChecking}
                          onClick={() => applyCode(video)}
                        >
                          {codeChecking ? '…' : 'Apply'}
                        </button>
                      </div>
                    )}
                    {rentMsg?.videoId === video._id && (
                      <span style={{ fontSize: '0.8rem', color: rentMsg.ok ? 'var(--color-success, #2e7d32)' : 'var(--color-error, #c62828)' }}>
                        {rentMsg.text}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {playerUrl && (
        <div className="video-player-overlay" onClick={() => setPlayerUrl(null)}>
          <div className="video-player" onClick={(e) => e.stopPropagation()}>
            <button className="video-player__close" onClick={() => setPlayerUrl(null)}>
              ×
            </button>
            <iframe src={playerUrl} title="Video player" allow="autoplay; fullscreen" allowFullScreen />
          </div>
        </div>
      )}
    </div>
  );
}
